import { formatDistance } from "date-fns";
import {
  MapPinIcon,
  BanknoteIcon,
  ScrollTextIcon,
  ClockIcon,
  Clock10Icon,
} from "lucide-react";
import { TypographyH3 } from "./ui/typography/h3";
import type { Job, School } from "~/db/types";
import { Badge } from "./ui/badge";
import { salaryTimePeriodsEnum, workingHoursEnum } from "~/utils/consts";
import ShareDropDown from "./shareDropdownMenu";
import { SaveButton } from "./saveButton";
import { ApplyButton } from "./applyButton";
import { useCurrentUserType } from "~/root";
import { Link } from "@remix-run/react";

export function JobPage({
  data,
  school,
}: {
  data: Job & { saved: number | null };
  school: Partial<School>;
}) {
  const userType = useCurrentUserType((state) => state.userType);

  const isRecruiter = userType === "recruiter";
  const isCandidate = userType === "candidate";
  return (
    <>
      <article>
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-5">
          <div>
            <TypographyH3 className="text-lime-800 font-bold mb-1">
              {data.title}
            </TypographyH3>
            <Link to={`../school/${data.schoolId}`}>
              <p className="mb-4 hover:underline">{data.schoolName}</p>
            </Link>
          </div>

          <ul className="flex items-center">
            {isRecruiter ? null : (
              <>
                <li>
                  <ApplyButton job={data} isCandidate={isCandidate} />
                </li>
                <li>
                  <SaveButton job={data} isCandidate={isCandidate} />
                </li>
              </>
            )}
            <li>
              <ShareDropDown job={data} />
            </li>
            {/* Disabling this for now */}
            {/* <li><MoreJobsDropDown /></li> */}
          </ul>
        </header>
        <ul className="flex flex-col gap-2 sm:gap-4 sm:flex-row py-4">
          <li className="flex items-center">
            <MapPinIcon className="mr-2 text-lime-600" size={18} />
            <p>{data.location}</p>
          </li>
          <li className="flex items-center">
            <BanknoteIcon className="mr-2 text-lime-600" size={18} />
            <p>
              ₹{" "}
              {data.salary?.split("-")[0] === "0" || data.salary === ""
                ? "Salary Negotiable"
                : data.salary +
                  " " +
                  salaryTimePeriodsEnum[
                    data.salaryTimePeriod as keyof typeof salaryTimePeriodsEnum
                  ]}{" "}
            </p>
          </li>
          <li className="flex items-center">
            <ScrollTextIcon className="mr-2 text-lime-600" size={18} />
            <p className="capitalize">{data.contractType}</p>
          </li>
          <li className="flex items-center">
            <ClockIcon className="mr-2 text-lime-600" size={18} />
            <p>
              {
                workingHoursEnum[
                  data.workingHours as keyof typeof workingHoursEnum
                ]
              }
            </p>
          </li>
        </ul>
        <ul className="mb-4">
          <li>
            <Badge variant="outline" className="bg-slate-100">
              <Clock10Icon size={16} className="mr-2" />
              {formatDistance(data.postedAt! * 1000, new Date(), {
                includeSeconds: false,
                addSuffix: true,
              })}
            </Badge>
          </li>
        </ul>
        <section className="pt-4 border-t border-gray-500/30">
          <div
            className="ql-editor -ml-4"
            dangerouslySetInnerHTML={{ __html: data.description }}
          />
        </section>
      </article>
    </>
  );
}
